import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useState, useEffect, useCallback, useMemo } from 'react';
import { isBoolean, isArray, isString } from 'lodash-es';
import PropTypes from 'prop-types';
import { StyledSwitch, IosSwitch, StyledFormControlLabel } from './Switch.styles.js';
const _excluded = ["label", "sx", "onChange", "labelPlacement", "checked", "isControlled", "color", "iosSwitch", "disabled", "checkedColor", "uncheckedColor", "disabledOpacity"];
function Switch(_ref) {
  let {
      label,
      sx,
      onChange,
      labelPlacement,
      checked,
      isControlled,
      color,
      iosSwitch,
      disabled,
      checkedColor,
      uncheckedColor,
      disabledOpacity
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  const [selfChecked, setSelfChecked] = useState(checked);
  useEffect(() => {
    if (isBoolean(checked)) {
      setSelfChecked(checked);
    }
  }, [checked]);
  const handleChange = useCallback(event => {
    if (onChange && isControlled) {
      onChange(event.target.checked, event);
      return;
    }
    setSelfChecked(event.target.checked);
    onChange(event.target.checked, event);
  }, [onChange]);
  const BasicSwitch = /*#__PURE__*/React__default.createElement(StyledSwitch, _extends({
    color: color,
    checked: selfChecked,
    onChange: handleChange,
    disabled: disabled
  }, otherProps));
  const labelText = useMemo(() => {
    if (/*#__PURE__*/React__default.isValidElement(label)) {
      return label;
    }
    if (isArray(label)) {
      var _label$;
      return selfChecked ? label[1] : (_label$ = label[0]) !== null && _label$ !== void 0 ? _label$ : '';
    }
    if (isString(label)) {
      return label;
    }
    return '';
  }, [label, selfChecked]);
  if (iosSwitch) {
    return /*#__PURE__*/React__default.createElement(IosSwitch, _extends({
      checked: selfChecked,
      onChange: handleChange,
      disabled: disabled,
      backgroundColor: {
        checkedColor,
        uncheckedColor
      },
      disabledOpacity: disabledOpacity
    }, otherProps));
  }
  if (!label) {
    return BasicSwitch;
  }
  return /*#__PURE__*/React__default.createElement(StyledFormControlLabel, {
    control: BasicSwitch,
    label: labelText,
    labelPlacement: labelPlacement,
    sx: sx
  });
}
Switch.propTypes = {
  /**
   * fixed text or an array with 2 options for switch status
   */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.instanceOf(Element), PropTypes.arrayOf(PropTypes.string, PropTypes.node, PropTypes.instanceOf(Element))]),
  /**
   * onChange callback function
   */
  onChange: PropTypes.func,
  /**
   * label position relative to switch button
   */
  labelPlacement: PropTypes.oneOf(['bottom', 'end', 'start', 'top']),
  /**
   * checked value from parent component
   */
  checked: PropTypes.bool,
  /**
   * Control by parent component
   */
  isControlled: PropTypes.bool,
  /**
   * Attribute Props into input
   */
  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   */
  iosSwitch: PropTypes.bool,
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'error', 'info', 'warning']),
  sx: PropTypes.shape({}),
  disabled: PropTypes.bool,
  checkedColor: PropTypes.string,
  uncheckedColor: PropTypes.string,
  disabledOpacity: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
Switch.defaultProps = {
  label: undefined,
  onChange: null,
  labelPlacement: 'end',
  checked: undefined,
  isControlled: true,
  color: 'secondary',
  iosSwitch: false,
  sx: {},
  disabled: false,
  checkedColor: '#7B61FF',
  uncheckedColor: 'rgba(0,0,0,.25)',
  disabledOpacity: 0.4
};
export { Switch as default };